// Engineer PWA "Next of kin" screen (Session AOU). A small, single-purpose form: the office's
// fallback contact if a lone-worker escalation calls the engineer directly and gets no answer
// (server/src/modules/loneworker/). The platform never contacts this number itself -- the office
// sees it only on the Lone Worker page's overdue row (LoneWorkerPage.tsx), for a human to ring by
// hand. Self-entry is the primary path here; an admin can also set or clear it in the engineer
// account panel (EngineersPage.tsx's Contact modal).
//
// GATED ON hasOwnEngineerRecords, NOT hasOwnMessageThread. This is Engineer-row data with no
// counterparty (like van stock and the timesheet), not a conversation (like Messages) -- see
// src/lib/pwaIdentity.ts's "two questions" note. That choice is load-bearing here specifically: the
// Director's own admin account holds a real Engineer row (Session AGW) that is invisible on the
// office Engineers page, so this screen is the ONLY place that row can ever set or clear this data.
import { useEffect, useState } from "react";
import { useAuth } from "../../lib/auth";
import { hasOwnEngineerRecords } from "../../lib/pwaIdentity";
import { OversightNotice } from "./OversightNotice";
import { api } from "../../lib/api";

const LABEL_CLASS = "mb-1 block text-sm text-ele-body";
const INPUT_CLASS =
  "w-full rounded-ele border border-ele-field-border bg-ele-field px-3 py-2 text-sm text-ele-strong outline-none focus:border-ele-green";

export function NextOfKin() {
  const { user } = useAuth();
  const ownRecords = hasOwnEngineerRecords(user);

  const [name, setName] = useState("");
  const [relationship, setRelationship] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!ownRecords) {
      setLoading(false);
      return;
    }
    api.engineers
      .myNextOfKin()
      .then((d) => {
        setName(d.nextOfKinName ?? "");
        setRelationship(d.nextOfKinRelationship ?? "");
        setPhone(d.nextOfKinPhone ?? "");
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, [ownRecords]);

  async function save() {
    setBusy(true);
    setSaveError(false);
    setSaved(false);
    try {
      await api.engineers.updateMyNextOfKin({
        nextOfKinName: name.trim(),
        nextOfKinRelationship: relationship.trim(),
        nextOfKinPhone: phone.trim(),
      });
      setSaved(true);
    } catch {
      setSaveError(true);
    } finally {
      setBusy(false);
    }
  }

  if (!ownRecords) {
    return (
      <OversightNotice
        title="Next of kin"
        explanation="This account has no engineer profile, so there is no next of kin contact to set here."
      />
    );
  }

  return (
    <div>
      <h1 className="mb-1 font-heading text-2xl tracking-wide text-ele-strong">Next of kin</h1>
      <p className="mb-4 text-sm text-ele-body">
        Optional. If you go quiet on a check-in and do not answer your phone, the office may use this to reach
        someone who knows where you are. We never contact them ourselves. This is only so the office can call by
        hand if they cannot reach you.
      </p>
      {loading ? (
        <p className="text-sm text-ele-body">Loading...</p>
      ) : loadError ? (
        <p role="alert" className="rounded-lg border border-ele-card-border bg-ele-card p-4 text-sm text-ele-strong">
          Could not load your next of kin details. Please try again in a moment.
        </p>
      ) : (
        <div className="space-y-3">
          <div>
            <label className={LABEL_CLASS} htmlFor="nok-name">
              Their name
            </label>
            <input
              id="nok-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Priya Sharma"
              className={INPUT_CLASS}
            />
          </div>
          <div>
            <label className={LABEL_CLASS} htmlFor="nok-relationship">
              Relationship to you
            </label>
            <input
              id="nok-relationship"
              value={relationship}
              onChange={(e) => setRelationship(e.target.value)}
              placeholder="e.g. wife, brother, partner"
              className={INPUT_CLASS}
            />
          </div>
          <div>
            <label className={LABEL_CLASS} htmlFor="nok-phone">
              Their phone number
            </label>
            <input
              id="nok-phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="e.g. 07700 900123"
              className={INPUT_CLASS}
            />
          </div>
          {saveError && (
            <p role="alert" className="text-sm text-ele-danger">
              Could not save these details. Please try again.
            </p>
          )}
          {saved && !saveError && (
            <p role="status" className="text-sm text-ele-green">
              Saved.
            </p>
          )}
          <button
            type="button"
            onClick={() => void save()}
            disabled={busy}
            className="min-h-11 rounded-ele bg-ele-green px-4 font-menu text-sm font-semibold tracking-wide text-ele-bg transition disabled:opacity-40"
          >
            {busy ? "Saving..." : "Save"}
          </button>
          <p className="text-xs text-ele-body opacity-70">Leave any field blank to clear it. Leaving all three blank is fine.</p>
        </div>
      )}
    </div>
  );
}
